import React, { useState, useRef } from 'react';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';
import { Subtitle, LinkGroup, Link } from 'Styles/links';
import RemoveScroll from '../../../RemoveScroll/RemoveScroll';
import { generateColumns } from './NavFlyout.utils';

import * as Styled from './NavFlyout.styled';
import { convertToCamelCase } from '../NavDrawer/NavDrawer.utils';

/**
 * A component that displays on hover of level 1 Navigation sections on Large + screen sizes.
 */
const NavFlyout = ({
  isOpen,
  level1Section,
  onBackdropEnter,
  onKeyDown,
  onMouseEnter,
  onMouseLeave
}) => {
  if (!isOpen) return null;

  const positionRef = useRef(null);
  const [positionFromTop, setPositionFromTop] = useState(0);

  const getPositionFromTop = () => {
    const { top } = positionRef.current.getBoundingClientRect();
    setPositionFromTop(top);
  };

  const { sections, menuTileGroup } = level1Section;
  const hasLeftContainer = level1Section?.links;

  const dataLabelName = convertToCamelCase(level1Section?.text);

  return (
    <Styled.NavFlyout onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <RemoveScroll>
        <FocusLock returnFocus>
          <Styled.LayoutContainer
            ref={positionRef}
            onMouseEnter={getPositionFromTop}
            positionFromTop={positionFromTop}
            data-label={`navFlyout_${dataLabelName}`}
          >
            <Styled.Layout onKeyDown={onKeyDown}>
              {hasLeftContainer && (
                <Styled.LeftContainer>
                  {level1Section.links
                    ?.filter((level3Section) => !level3Section.hideOnDesktop)
                    .map((level3Section) => (
                      <React.Fragment key={level3Section.id}>
                        {level3Section.text && (
                          <Subtitle accentFont>{level3Section.text}</Subtitle>
                        )}
                        <LinkGroup variant="mini" paddingSize="lg">
                          {level3Section.links
                            ?.filter((link) => !link.hideOnDesktop)
                            .map((link) => (
                              <Link
                                as={
                                  link.url && !link.url.includes('/help')
                                    ? NextLink
                                    : Link
                                }
                                href={link.url}
                                color={link.color}
                                variant="mini"
                                key={link.id}
                              >
                                {link.text}
                              </Link>
                            ))}
                        </LinkGroup>
                      </React.Fragment>
                    ))}
                </Styled.LeftContainer>
              )}
              <Styled.RightContainer hasLeftContainer={hasLeftContainer}>
                {generateColumns({
                  sections: sections || [],
                  menuTileGroup: menuTileGroup || []
                })}
              </Styled.RightContainer>
            </Styled.Layout>
          </Styled.LayoutContainer>
        </FocusLock>
      </RemoveScroll>
      <Styled.Backdrop onMouseEnter={onBackdropEnter} />
    </Styled.NavFlyout>
  );
};

NavFlyout.propTypes = {
  /**
   * Renders Navigation Flyout if true
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   *  Individual Level 1 Navigation Sections (Women, Men, Kids, About Us, Help)
   */
  level1Section: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
    text: PropTypes.string,
    name: PropTypes.string,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        color: PropTypes.string,
        id: PropTypes.string,
        url: PropTypes.string,
        text: PropTypes.string
      })
    ),
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        text: PropTypes.string,
        name: PropTypes.string,
        sections: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            text: PropTypes.string,
            links: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string,
                url: PropTypes.string,
                text: PropTypes.string
              })
            )
          })
        )
      })
    ),
    menuTileGroup: PropTypes.shape({
      type: PropTypes.string,
      tiles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
          url: PropTypes.string,
          image: PropTypes.shape({
            file: PropTypes.shape({
              url: PropTypes.string,
              details: PropTypes.shape({
                image: PropTypes.shape({
                  height: PropTypes.number,
                  width: PropTypes.number
                })
              })
            })
          })
        })
      )
    })
  }),
  /**
   * Handles closing of Navigation Flyout and Scroll Lock when cursor leaves component
   */
  onBackdropEnter: PropTypes.func,
  /**
   * Handles closing of Navigation Flyout and Scroll Lock when the Escape key is pressed
   */
  onKeyDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};

NavFlyout.defaultProps = {
  level1Section: null,
  onBackdropEnter: null,
  onKeyDown: null,
  onMouseEnter: null,
  onMouseLeave: null
};

export default NavFlyout;
